import { useEffect, useState, useMemo, useCallback } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Users, Package, CreditCard, TrendingUp, Trash2, Plus, Gift,
  Clock, AlertCircle, Pause, Play, MessageCircle, CheckCircle2, XCircle, Search,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

function StatCard({ icon: Icon, label, value, tone = "gold" }) {
  const tones = {
    gold: "text-dd-gold",
    orange: "text-amber-500",
    red: "text-red-500",
    green: "text-green-500",
    blue: "text-sky-400",
  };
  return (
    <div className="bg-white border border-dd-line rounded-xl p-5 shadow-sm">
      <div className="flex items-center gap-2 text-dd-ink-muted">
        <Icon className={`w-4 h-4 ${tones[tone]}`} strokeWidth={1.5} />
        <span className="dd-label">{label}</span>
      </div>
      <div className="font-serif text-4xl text-dd-ink mt-2">{value}</div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [todayStats, setTodayStats] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [subs, setSubs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [videos, setVideos] = useState([]);
  const [offers, setOffers] = useState([]);
  const [referralData, setReferralData] = useState({ referrals: [], leaderboard: [], total: 0 });
  const [bookings, setBookings] = useState([]);
  const [chatActions, setChatActions] = useState([]);
  const [tickets, setTickets] = useState([]);

  const [newT, setNewT] = useState({ name: "", location: "", rating: 5, text: "" });
  const [newF, setNewF] = useState({ question: "", answer: "", order: 0 });
  const [newV, setNewV] = useState({ title: "", description: "", youtube_id: "", video_url: "", thumbnail: "" });
  const [newO, setNewO] = useState({ title: "", subtitle: "", discount_percent: "", valid_till: "", active: true });

  // Bookings filters
  const [bQ, setBQ] = useState("");
  const [bStatus, setBStatus] = useState("all");
  const [bPage, setBPage] = useState(1);
  const PAGE_SIZE = 50;

  const load = useCallback(async () => {
    const [a, b, c, d, t, f, v, o, r, ts, bk, ca, tk] = await Promise.all([
      api.get("/admin/stats"),
      api.get("/admin/customers"),
      api.get("/admin/subscriptions"),
      api.get("/admin/orders"),
      api.get("/testimonials"),
      api.get("/faqs"),
      api.get("/videos"),
      api.get("/admin/offers"),
      api.get("/admin/referrals").catch((err) => { console.warn("admin/referrals failed:", err?.message); return { data: { referrals: [], leaderboard: [], total: 0 } }; }),
      api.get("/admin/today-stats").catch((err) => { console.warn("admin/today-stats failed:", err?.message); return { data: null }; }),
      api.get("/admin/bookings").catch((err) => { console.warn("admin/bookings failed:", err?.message); return { data: [] }; }),
      api.get("/admin/chatbot-actions").catch((err) => { console.warn("admin/chatbot-actions failed:", err?.message); return { data: [] }; }),
      api.get("/admin/support-tickets").catch((err) => { console.warn("admin/support-tickets failed:", err?.message); return { data: [] }; }),
    ]);
    setStats(a.data); setCustomers(b.data); setSubs(c.data); setOrders(d.data);
    setTestimonials(t.data); setFaqs(f.data); setVideos(v.data); setOffers(o.data);
    setReferralData(r.data);
    setTodayStats(ts.data); setBookings(bk.data); setChatActions(ca.data); setTickets(tk.data);
  }, []);
  useEffect(() => { load(); }, [load]);
  // Auto-refresh every 60s
  useEffect(() => {
    const id = setInterval(() => load(), 60000);
    return () => clearInterval(id);
  }, [load]);

  const filteredBookings = useMemo(() => {
    let list = bookings;
    if (bStatus !== "all") list = list.filter((b) => b.status === bStatus);
    if (bQ.trim()) {
      const q = bQ.trim().toLowerCase();
      list = list.filter((b) =>
        (b.name || "").toLowerCase().includes(q) || (b.phone || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [bookings, bQ, bStatus]);
  const pagedBookings = filteredBookings.slice((bPage - 1) * PAGE_SIZE, bPage * PAGE_SIZE);
  const totalBookingPages = Math.max(1, Math.ceil(filteredBookings.length / PAGE_SIZE));

  const addT = async (e) => { e.preventDefault(); await api.post("/admin/testimonials", newT); setNewT({ name: "", location: "", rating: 5, text: "" }); load(); };
  const delT = async (id) => { await api.delete(`/admin/testimonials/${id}`); load(); };
  const addF = async (e) => { e.preventDefault(); await api.post("/admin/faqs", newF); setNewF({ question: "", answer: "", order: 0 }); load(); };
  const delF = async (id) => { await api.delete(`/admin/faqs/${id}`); load(); };
  const addV = async (e) => { e.preventDefault(); await api.post("/admin/videos", newV); setNewV({ title: "", description: "", youtube_id: "", video_url: "", thumbnail: "" }); load(); };
  const delV = async (id) => { await api.delete(`/admin/videos/${id}`); load(); };
  const addO = async (e) => {
    e.preventDefault();
    await api.post("/admin/offers", {
      ...newO,
      discount_percent: newO.discount_percent === "" ? null : Number(newO.discount_percent),
    });
    setNewO({ title: "", subtitle: "", discount_percent: "", valid_till: "", active: true });
    load();
  };
  const delO = async (id) => { await api.delete(`/admin/offers/${id}`); load(); };
  const toggleO = async (o) => { await api.patch(`/admin/offers/${o.id}`, { active: !o.active }); load(); };

  // Booking actions
  const confirmBooking = async (b) => { await api.patch(`/admin/bookings/${b.id}`, { status: "confirmed" }); load(); };
  const cancelBooking  = async (b) => { await api.patch(`/admin/bookings/${b.id}`, { status: "cancelled" }); load(); };
  const whatsappBooking = (b) => {
    const phone = (b.phone || "").replace(/\D/g, "").replace(/^0+/, "");
    const ph = phone.length === 10 ? `91${phone}` : phone;
    const msg = `Namaste ${b.name || ""} ji! Aapki Divya Dhara booking confirm ho gayi hai. 🥛🙏`;
    window.open(`https://wa.me/${ph}?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };
  const resolveTicket = async (t) => { await api.patch(`/admin/support-tickets/${t.id}`, { status: "resolved" }); load(); };

  return (
    <div className="bg-[#0F1419] min-h-screen" data-testid="admin-dashboard">
      <Navbar />
      <div className="pt-28 dd-container pb-20">
        <div className="flex items-end justify-between flex-col md:flex-row gap-3 border-b border-white/10 pb-6">
          <div>
            <div className="dd-label text-dd-gold flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" /> Admin Control Room · Live
            </div>
            <h1 className="font-serif text-3xl md:text-4xl text-dd-cream mt-1">Welcome, {user?.name}</h1>
            <p className="text-xs text-dd-cream-muted mt-1">Matrubhumi Gauras Bhandar · Owner Portal</p>
          </div>
          <div className="text-xs text-dd-cream-muted">
            {new Date().toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
            <StatCard icon={Users} label="Customers" value={stats.customers} />
            <StatCard icon={Package} label="Active Subs" value={stats.subscriptions_active} />
            <StatCard icon={CreditCard} label="Paid Orders" value={stats.orders_paid} />
            <StatCard icon={TrendingUp} label="Revenue" value={`₹${stats.revenue?.toLocaleString("en-IN")}`} />
          </div>
        )}

        {todayStats && (
          <>
            <div className="dd-label text-dd-gold mt-10 mb-3">📊 Today at a Glance</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Package} label="📦 Aaj ki New Bookings" value={todayStats.today_bookings} tone="blue" />
              <StatCard icon={Pause}   label="⏸️ Aaj Pause Kiye" value={todayStats.today_pauses} tone="orange" />
              <StatCard icon={AlertCircle} label="🎫 Open Support Tickets" value={todayStats.open_tickets} tone="red" />
              <StatCard icon={Users} label="👥 Total Active Customers" value={todayStats.active_customers} tone="green" />
            </div>
          </>
        )}

        <Tabs defaultValue={todayStats?.new_bookings_pending ? "bookings" : "customers"} className="mt-10">
          <TabsList className="bg-white border border-dd-line rounded-sm p-1 overflow-x-auto no-scrollbar flex w-auto">
            <TabsTrigger value="bookings" data-testid="admin-tab-bookings">
              📦 New Bookings
              {todayStats?.new_bookings_pending > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-[0.58rem] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] inline-flex items-center justify-center">
                  {todayStats.new_bookings_pending}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="pauseresume" data-testid="admin-tab-pauseresume">⏸️ Pause/Resume</TabsTrigger>
            <TabsTrigger value="tickets" data-testid="admin-tab-tickets">
              🎫 Support Tickets
              {todayStats?.open_tickets > 0 && (
                <span className="ml-1.5 bg-red-500 text-white text-[0.58rem] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] inline-flex items-center justify-center">
                  {todayStats.open_tickets}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="customers" data-testid="admin-tab-customers">Customers</TabsTrigger>
            <TabsTrigger value="subs" data-testid="admin-tab-subs">Subscriptions</TabsTrigger>
            <TabsTrigger value="orders" data-testid="admin-tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="referrals" data-testid="admin-tab-referrals">Customer Referrals</TabsTrigger>
            <TabsTrigger value="testimonials" data-testid="admin-tab-testimonials">Testimonials</TabsTrigger>
            <TabsTrigger value="faqs" data-testid="admin-tab-faqs">FAQs</TabsTrigger>
            <TabsTrigger value="videos" data-testid="admin-tab-videos">Videos</TabsTrigger>
            <TabsTrigger value="offers" data-testid="admin-tab-offers">Offers</TabsTrigger>
          </TabsList>

          {/* 📦 New Bookings */}
          <TabsContent value="bookings">
            <div className="bg-white border border-dd-line rounded-xl p-4 mb-3 flex gap-3 flex-wrap items-center" data-testid="bookings-toolbar">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-dd-ink-muted" />
                <input
                  value={bQ}
                  onChange={(e) => { setBQ(e.target.value); setBPage(1); }}
                  placeholder="Search by name or phone…"
                  className="w-full pl-9 pr-3 py-2 border border-dd-line rounded-sm text-sm"
                  data-testid="bookings-search"
                />
              </div>
              <select value={bStatus} onChange={(e) => { setBStatus(e.target.value); setBPage(1); }}
                className="border border-dd-line rounded-sm px-3 py-2 text-sm" data-testid="bookings-filter">
                <option value="all">All Status</option>
                <option value="new">🟡 New</option>
                <option value="confirmed">🟢 Confirmed</option>
                <option value="cancelled">🔴 Cancelled</option>
              </select>
              <div className="text-xs text-dd-ink-muted">{filteredBookings.length} of {bookings.length} bookings</div>
            </div>
            <div className="bg-white border border-dd-line rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F2] text-dd-ink-muted dd-label">
                  <tr>
                    <th className="p-3 text-left">#</th><th className="p-3 text-left">Naam</th><th className="p-3 text-left">Phone</th>
                    <th className="p-3 text-left">Product</th><th className="p-3 text-left">Qty</th>
                    <th className="p-3 text-left">Delivery</th><th className="p-3 text-left">Address</th>
                    <th className="p-3 text-left">Date-Time</th><th className="p-3 text-left">Status</th>
                    <th className="p-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedBookings.map((b, i) => {
                    const statusBadge = {
                      new: "bg-yellow-100 text-yellow-800",
                      confirmed: "bg-green-100 text-green-800",
                      cancelled: "bg-red-100 text-red-800",
                    }[b.status] || "bg-gray-100 text-gray-700";
                    return (
                      <tr key={b.id} className="border-t border-dd-line align-top" data-testid={`booking-row-${b.id}`}>
                        <td className="p-3">{(bPage - 1) * PAGE_SIZE + i + 1}</td>
                        <td className="p-3 font-medium">{b.name}</td>
                        <td className="p-3"><a className="text-[#8F6E28] hover:underline" href={`tel:${b.phone}`}>{b.phone}</a></td>
                        <td className="p-3">{b.product}</td>
                        <td className="p-3">{b.quantity}</td>
                        <td className="p-3">{b.delivery_type}</td>
                        <td className="p-3 max-w-xs truncate" title={b.address}>{b.address}</td>
                        <td className="p-3 text-xs text-dd-ink-muted">{b.created_at?.replace("T", " ").slice(0, 16)}</td>
                        <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${statusBadge}`}>{b.status === "new" ? "🟡 New" : b.status === "confirmed" ? "🟢 Confirmed" : "🔴 Cancelled"}</span></td>
                        <td className="p-3">
                          <div className="flex gap-1.5 flex-wrap">
                            {b.status !== "confirmed" && (
                              <button onClick={() => confirmBooking(b)} className="text-xs bg-green-600 hover:bg-green-700 text-white px-2.5 py-1 rounded-sm flex items-center gap-1" data-testid={`confirm-${b.id}`}>
                                <CheckCircle2 className="w-3 h-3" /> Confirm
                              </button>
                            )}
                            <button onClick={() => whatsappBooking(b)} className="text-xs bg-[#25D366] hover:bg-[#1faa52] text-white px-2.5 py-1 rounded-sm flex items-center gap-1" data-testid={`wa-${b.id}`}>
                              <MessageCircle className="w-3 h-3" /> WhatsApp
                            </button>
                            {b.status !== "cancelled" && (
                              <button onClick={() => cancelBooking(b)} className="text-xs border border-red-300 text-red-600 hover:bg-red-50 px-2.5 py-1 rounded-sm flex items-center gap-1" data-testid={`cancel-${b.id}`}>
                                <XCircle className="w-3 h-3" /> Cancel
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {filteredBookings.length === 0 && <tr><td className="p-6 text-center text-dd-ink-muted" colSpan={10}>No bookings match your filters.</td></tr>}
                </tbody>
              </table>
              {totalBookingPages > 1 && (
                <div className="p-3 border-t border-dd-line flex gap-2 justify-end items-center text-xs">
                  <button disabled={bPage <= 1} onClick={() => setBPage(bPage - 1)} className="px-3 py-1 border border-dd-line rounded-sm disabled:opacity-40">← Prev</button>
                  <span className="text-dd-ink-muted">Page {bPage} of {totalBookingPages}</span>
                  <button disabled={bPage >= totalBookingPages} onClick={() => setBPage(bPage + 1)} className="px-3 py-1 border border-dd-line rounded-sm disabled:opacity-40">Next →</button>
                </div>
              )}
            </div>
          </TabsContent>

          {/* ⏸️ Pause/Resume Log */}
          <TabsContent value="pauseresume">
            <div className="bg-white border border-dd-line rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F2] text-dd-ink-muted dd-label">
                  <tr><th className="p-3 text-left">#</th><th className="p-3 text-left">Naam</th><th className="p-3 text-left">Phone</th><th className="p-3 text-left">Action</th><th className="p-3 text-left">Date-Time</th></tr>
                </thead>
                <tbody>
                  {chatActions.map((a, i) => (
                    <tr key={a.id} className="border-t border-dd-line" data-testid={`action-row-${a.id}`}>
                      <td className="p-3">{i + 1}</td>
                      <td className="p-3">{a.name || "—"}</td>
                      <td className="p-3">{a.phone || "—"}</td>
                      <td className="p-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${a.action === "PAUSED" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"}`}>
                          {a.action === "PAUSED" ? "⏸️ PAUSED" : "▶️ RESUMED"}
                        </span>
                      </td>
                      <td className="p-3 text-xs text-dd-ink-muted">{a.created_at?.replace("T", " ").slice(0, 16)}</td>
                    </tr>
                  ))}
                  {chatActions.length === 0 && <tr><td className="p-6 text-center text-dd-ink-muted" colSpan={5}>No pause/resume actions yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* 🎫 Support Tickets */}
          <TabsContent value="tickets">
            <div className="bg-white border border-dd-line rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F2] text-dd-ink-muted dd-label">
                  <tr><th className="p-3 text-left">#</th><th className="p-3 text-left">Naam</th><th className="p-3 text-left">Phone</th><th className="p-3 text-left">Issue</th><th className="p-3 text-left">Date-Time</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Action</th></tr>
                </thead>
                <tbody>
                  {tickets.map((t, i) => (
                    <tr key={t.id} className="border-t border-dd-line" data-testid={`ticket-row-${t.id}`}>
                      <td className="p-3">{i + 1}</td>
                      <td className="p-3">{t.name || "—"}</td>
                      <td className="p-3">{t.phone || "—"}</td>
                      <td className="p-3">{t.issue_type}</td>
                      <td className="p-3 text-xs text-dd-ink-muted">{t.created_at?.replace("T", " ").slice(0, 16)}</td>
                      <td className="p-3"><span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${t.status === "resolved" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>{t.status === "resolved" ? "🟢 Resolved" : "🔴 Open"}</span></td>
                      <td className="p-3">
                        {t.status !== "resolved" && (
                          <button onClick={() => resolveTicket(t)} className="text-xs bg-green-600 hover:bg-green-700 text-white px-2.5 py-1 rounded-sm flex items-center gap-1" data-testid={`resolve-${t.id}`}>
                            <CheckCircle2 className="w-3 h-3" /> Mark Resolved
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {tickets.length === 0 && <tr><td className="p-6 text-center text-dd-ink-muted" colSpan={7}>No support tickets yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="customers">
            <div className="bg-white border border-dd-line rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F2] text-dd-ink-muted dd-label">
                  <tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Phone</th><th className="p-3 text-left">Wallet</th><th className="p-3 text-left">Ref Code</th><th className="p-3 text-left">Joined</th></tr>
                </thead>
                <tbody>
                  {customers.map((c) => (
                    <tr key={c.id} className="border-t border-dd-line">
                      <td className="p-3">{c.name}</td>
                      <td className="p-3">{c.email}</td>
                      <td className="p-3">{c.phone}</td>
                      <td className="p-3 text-dd-gold font-semibold">₹{c.wallet_balance || 0}</td>
                      <td className="p-3 font-mono text-xs">{c.referral_code || "—"}</td>
                      <td className="p-3 text-dd-ink-muted">{c.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                  {customers.length === 0 && <tr><td className="p-6 text-dd-ink-muted" colSpan={6}>No customers yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="subs">
            <div className="bg-white border border-dd-line rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F2] text-dd-ink-muted dd-label"><tr><th className="p-3 text-left">Plan</th><th className="p-3 text-left">Price</th><th className="p-3 text-left">Pincode</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Start</th></tr></thead>
                <tbody>
                  {subs.map((s) => (
                    <tr key={s.id} className="border-t border-dd-line">
                      <td className="p-3">{s.plan_name}</td><td className="p-3">₹{s.price}</td><td className="p-3">{s.pincode}</td>
                      <td className="p-3 capitalize">{s.status}</td><td className="p-3">{s.start_date}</td>
                    </tr>
                  ))}
                  {subs.length === 0 && <tr><td className="p-6 text-dd-ink-muted" colSpan={5}>No subscriptions yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="orders">
            <div className="bg-white border border-dd-line rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF7F2] text-dd-ink-muted dd-label"><tr><th className="p-3 text-left">Order</th><th className="p-3 text-left">Plan</th><th className="p-3 text-left">Amount</th><th className="p-3 text-left">Status</th><th className="p-3 text-left">Created</th></tr></thead>
                <tbody>
                  {orders.map((o) => (
                    <tr key={o.id} className="border-t border-dd-line">
                      <td className="p-3 font-mono text-xs">{o.razorpay_order_id}</td>
                      <td className="p-3">{o.plan_slug}</td>
                      <td className="p-3">₹{o.amount}</td>
                      <td className="p-3 capitalize"><span className={`px-2 py-0.5 rounded-sm text-xs ${o.status === "paid" ? "bg-dd-green/10 text-dd-green" : "bg-dd-gold-soft/60 text-[#8F6E28]"}`}>{o.status}</span></td>
                      <td className="p-3 text-dd-ink-muted">{o.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                  {orders.length === 0 && <tr><td className="p-6 text-dd-ink-muted" colSpan={5}>No orders yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="referrals">
            <div className="grid lg:grid-cols-5 gap-5">
              <div className="lg:col-span-2 bg-white border border-dd-line rounded-xl p-5">
                <h3 className="font-serif text-2xl text-dd-ink flex items-center gap-2 mb-4">
                  <Gift className="w-5 h-5 text-dd-gold" /> Top Referrers
                </h3>
                {referralData.leaderboard?.length === 0 ? (
                  <div className="text-sm text-dd-ink-muted">No referrals yet.</div>
                ) : (
                  <ol className="space-y-3" data-testid="referral-leaderboard">
                    {referralData.leaderboard.slice(0, 10).map((l, i) => (
                      <li key={i} className="flex items-center justify-between gap-3 border-b border-dd-line pb-2 last:border-0">
                        <div className="flex items-center gap-3">
                          <span className="w-7 h-7 rounded-full bg-dd-gold/20 text-dd-gold font-bold text-xs flex items-center justify-center">#{i + 1}</span>
                          <div>
                            <div className="font-medium text-dd-ink">{l.name}</div>
                            <div className="text-xs text-dd-ink-muted font-mono">{l.referral_code}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="font-serif text-2xl text-dd-orange">{l.total_referrals}</div>
                          <div className="text-[0.6rem] text-dd-ink-muted">₹{l.wallet_balance} wallet</div>
                        </div>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
              <div className="lg:col-span-3 bg-white border border-dd-line rounded-xl overflow-hidden">
                <div className="p-5 border-b border-dd-line flex items-center justify-between">
                  <h3 className="font-serif text-xl text-dd-ink">All Referrals ({referralData.total})</h3>
                  <span className="dd-label text-dd-gold">₹100 per signup · paid to both</span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-[#FAF7F2] text-dd-ink-muted dd-label">
                    <tr><th className="p-3 text-left">Referrer</th><th className="p-3 text-left">Friend Signed Up</th><th className="p-3 text-left">Code</th><th className="p-3 text-left">Bonus</th><th className="p-3 text-left">Date</th></tr>
                  </thead>
                  <tbody>
                    {referralData.referrals.map((r) => (
                      <tr key={r.id} className="border-t border-dd-line" data-testid={`referral-row-${r.id}`}>
                        <td className="p-3">{r.referrer_name || "—"}</td>
                        <td className="p-3">{r.referee_name}<div className="text-xs text-dd-ink-muted">{r.referee_email}</div></td>
                        <td className="p-3 font-mono text-xs">{r.code_used}</td>
                        <td className="p-3 text-dd-green font-semibold">₹{r.bonus}</td>
                        <td className="p-3 text-dd-ink-muted">{r.created_at?.slice(0, 10)}</td>
                      </tr>
                    ))}
                    {referralData.referrals.length === 0 && <tr><td className="p-6 text-dd-ink-muted" colSpan={5}>No referrals yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="testimonials">
            <form onSubmit={addT} className="bg-white border border-dd-line rounded-xl p-5 grid md:grid-cols-5 gap-3" data-testid="admin-testimonial-form">
              <input required placeholder="Name" value={newT.name} onChange={(e) => setNewT({ ...newT, name: e.target.value })} className="border border-dd-line px-3 py-2 rounded-sm" />
              <input placeholder="Location" value={newT.location} onChange={(e) => setNewT({ ...newT, location: e.target.value })} className="border border-dd-line px-3 py-2 rounded-sm" />
              <input type="number" min={1} max={5} value={newT.rating} onChange={(e) => setNewT({ ...newT, rating: Number(e.target.value) })} className="border border-dd-line px-3 py-2 rounded-sm" />
              <input required placeholder="Text" value={newT.text} onChange={(e) => setNewT({ ...newT, text: e.target.value })} className="border border-dd-line px-3 py-2 rounded-sm md:col-span-2" />
              <button type="submit" className="bg-dd-green text-dd-cream px-4 py-2 rounded-sm flex items-center gap-1.5 justify-center md:col-span-5"><Plus className="w-4 h-4" /> Add Testimonial</button>
            </form>
            <div className="mt-4 space-y-2">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-white border border-dd-line p-4 rounded-xl flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium">{t.name} · <span className="text-dd-ink-muted text-sm">{t.location}</span></div>
                    <div className="text-sm text-dd-ink-muted">{t.text}</div>
                  </div>
                  <button onClick={() => delT(t.id)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="faqs">
            <form onSubmit={addF} className="bg-white border border-dd-line rounded-xl p-5 grid gap-3" data-testid="admin-faq-form">
              <input required placeholder="Question" value={newF.question} onChange={(e) => setNewF({ ...newF, question: e.target.value })} className="border border-dd-line px-3 py-2 rounded-sm" />
              <textarea required placeholder="Answer" value={newF.answer} onChange={(e) => setNewF({ ...newF, answer: e.target.value })} rows={3} className="border border-dd-line px-3 py-2 rounded-sm" />
              <button type="submit" className="bg-dd-green text-dd-cream px-4 py-2 rounded-sm flex items-center gap-1.5 justify-center"><Plus className="w-4 h-4" /> Add FAQ</button>
            </form>
            <div className="mt-4 space-y-2">
              {faqs.map((f) => (
                <div key={f.id} className="bg-white border border-dd-line p-4 rounded-xl flex items-start justify-between gap-4">
                  <div>
                    <div className="font-medium">{f.question}</div>
                    <div className="text-sm text-dd-ink-muted mt-1">{f.answer}</div>
                  </div>
                  <button onClick={() => delF(f.id)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="videos">
            <form onSubmit={addV} className="bg-white border border-dd-line rounded-xl p-5 grid md:grid-cols-2 gap-3" data-testid="admin-video-form">
              <input required placeholder="Title" value={newV.title} onChange={(e) => setNewV({ ...newV, title: e.target.value })} className="border border-dd-line px-3 py-2 rounded-sm" />
              <input placeholder="YouTube ID (optional, e.g. dQw4w9WgXcQ)" value={newV.youtube_id} onChange={(e) => setNewV({ ...newV, youtube_id: e.target.value })} className="border border-dd-line px-3 py-2 rounded-sm" />
              <input placeholder="Video URL (MP4, optional — takes priority over YouTube ID)" value={newV.video_url || ""} onChange={(e) => setNewV({ ...newV, video_url: e.target.value })} className="border border-dd-line px-3 py-2 rounded-sm md:col-span-2" />
              <input placeholder="Description" value={newV.description} onChange={(e) => setNewV({ ...newV, description: e.target.value })} className="border border-dd-line px-3 py-2 rounded-sm md:col-span-2" />
              <input placeholder="Thumbnail URL (optional)" value={newV.thumbnail} onChange={(e) => setNewV({ ...newV, thumbnail: e.target.value })} className="border border-dd-line px-3 py-2 rounded-sm md:col-span-2" />
              <button type="submit" className="bg-dd-green text-dd-cream px-4 py-2 rounded-sm flex items-center gap-1.5 justify-center md:col-span-2"><Plus className="w-4 h-4" /> Add Video</button>
            </form>
            <div className="mt-4 grid md:grid-cols-2 gap-3">
              {videos.map((v) => (
                <div key={v.id} className="bg-white border border-dd-line p-4 rounded-xl flex items-start justify-between gap-3">
                  <div>
                    <div className="font-medium">{v.title}</div>
                    <div className="text-xs text-dd-ink-muted font-mono">{v.youtube_id}</div>
                  </div>
                  <button onClick={() => delV(v.id)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="offers">
            <form onSubmit={addO} className="bg-white border border-dd-line rounded-xl p-5 grid md:grid-cols-2 gap-3" data-testid="admin-offer-form">
              <input required placeholder="Offer title (e.g. First Litre at Rs 80)" value={newO.title} onChange={(e) => setNewO({ ...newO, title: e.target.value })} className="border border-dd-line px-3 py-2 rounded-sm md:col-span-2" />
              <input placeholder="Subtitle (optional)" value={newO.subtitle} onChange={(e) => setNewO({ ...newO, subtitle: e.target.value })} className="border border-dd-line px-3 py-2 rounded-sm md:col-span-2" />
              <input type="number" placeholder="Discount % (optional)" value={newO.discount_percent} onChange={(e) => setNewO({ ...newO, discount_percent: e.target.value })} className="border border-dd-line px-3 py-2 rounded-sm" />
              <input type="date" placeholder="Valid till" value={newO.valid_till} onChange={(e) => setNewO({ ...newO, valid_till: e.target.value })} className="border border-dd-line px-3 py-2 rounded-sm" />
              <label className="flex items-center gap-2 md:col-span-2 text-sm text-dd-ink">
                <input type="checkbox" checked={newO.active} onChange={(e) => setNewO({ ...newO, active: e.target.checked })} /> Active (show in site ribbon immediately)
              </label>
              <button type="submit" className="bg-dd-orange text-white px-4 py-2 rounded-sm flex items-center gap-1.5 justify-center md:col-span-2 font-semibold"><Plus className="w-4 h-4" /> Add Offer</button>
            </form>
            <div className="mt-4 space-y-2">
              {offers.map((o) => (
                <div key={o.id} className="bg-white border border-dd-line p-4 rounded-xl flex items-start justify-between gap-4" data-testid={`offer-row-${o.id}`}>
                  <div>
                    <div className="font-semibold text-dd-ink">{o.title} {o.discount_percent ? <span className="ml-2 text-xs bg-dd-orange text-white rounded-full px-2 py-0.5">{o.discount_percent}% OFF</span> : null}</div>
                    {o.subtitle && <div className="text-sm text-dd-ink-muted mt-1">{o.subtitle}</div>}
                    <div className="text-xs text-dd-ink-muted mt-1">Valid till: {o.valid_till || "—"}  ·  Status: <span className={o.active ? "text-dd-green font-medium" : "text-red-600"}>{o.active ? "Active" : "Inactive"}</span></div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => toggleO(o)} className={`text-xs px-3 py-1.5 rounded-sm border ${o.active ? "border-red-300 text-red-600 hover:bg-red-50" : "border-dd-green text-dd-green hover:bg-dd-green hover:text-white"}`}>
                      {o.active ? "Disable" : "Enable"}
                    </button>
                    <button onClick={() => delO(o.id)} className="text-red-600 hover:text-red-700"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </div>
      <Footer />
    </div>
  );
}
